import React, { useRef, useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import GoogleMapReact from 'google-map-react';
import { getGradeColor, getGradeAGradient, convertCurrency, isWithinBounds } from 'src/utils/functions';
import { Bounds } from 'src/interfaces/idashboard';
import { getMarketsInMapViewport } from 'src/store/Dashboard/actions';
import { useDispatch } from 'react-redux';
import { abbreviateNumber } from 'src/utils/functions';
import { Box, Grid, Skeleton } from '@mui/material';
import { GOOGLE_MAPS_API_KEY } from 'src/utils/keys';
import { mapOptions } from 'src/utils/functions';
import { MarketPointer } from 'src/App/Dashboards/Market/Components/Pointer';
import { exchangeRates } from 'src/utils/const';

const formatMetricValue = (value: any, metric: string, currency: string) => {
  if (metric === 'supply') return `${abbreviateNumber(value)} listings`;
  if (['revenue', 'adr', 'rent_gap'].includes(metric))
    return `${exchangeRates[currency].symbol}${abbreviateNumber(convertCurrency(currency, value))}`;
  if (['occupancy_rate', 'short_term_rental_yield'].includes(metric)) return `${value}%`;
  if (metric == 'market_grade') {
    const isGradeA = (value || '').toUpperCase().startsWith('A');
    return (
      <Box 
        sx={{ 
          color: isGradeA ? undefined : getGradeColor(value), 
          fontWeight: 'bold', 
          display: 'inline-block',
          ...(isGradeA && getGradeAGradient()),
        }}
      >
        {value}
      </Box>
    );
  }
  return value;
};

const MapComponent = ({
  markets,
  isLoading,
  isMobileScreen,
  showFullMap,
  currentRequestType,
  selectedSortBy,
  currency,
}: {
  markets: any[];
  isLoading: boolean;
  isMobileScreen: boolean;
  showFullMap: boolean;
  currentRequestType: string;
  selectedSortBy: string;
  currency: string;
}) => {
  const mapRef = useRef<any>(null);
  const mapsRef = useRef<any>(null);
  const maxMarketsToShow = isMobileScreen ? 3 : showFullMap ? 20 : 7;
  const marketsInMapViewport = useSelector((state: any) => state.dashboard.marketsInMapViewport);

  const [bounds, setBounds] = useState<GoogleMapReact.Bounds | null>(null);
  const [dedupedDynamicMarkets, setDedupedDynamicMarkets] = useState<any[]>([]);
  const [visibleRankedMarkets, setVisibleRankedMarkets] = useState<any[]>([]);

  const dispatch = useDispatch();

  useEffect(() => {
    if (!markets || !markets.length || !marketsInMapViewport.data) return;
    const rankedMarketIDs = new Set(markets.map(market => market.market_id));
    const dedupedDynamicMarkets = marketsInMapViewport.data.filter(
      market => !rankedMarketIDs.has(market.market_id)
    );
    setDedupedDynamicMarkets(dedupedDynamicMarkets);
  }, [markets, marketsInMapViewport]);

  useEffect(() => {
    if (bounds) {
      // get markets within bounds
      const visibleMarkets = markets.filter(market => {
        return isWithinBounds({ lat: market.latitude, lng: market.longitude }, bounds);
      });
      setVisibleRankedMarkets(visibleMarkets);
    } else {
      setVisibleRankedMarkets(markets);
    }
  }, [bounds, markets]);

  const apiIsLoaded = (map: any, maps: any, markets: any[]) => {
    if (!map || !maps || !markets || markets.length === 0) {
      return;
    }

    const bounds = new maps.LatLngBounds();

    markets.forEach(market => {
      // showing only top 10 markets
      if (market.rank > 10) return;

      try {
        const latitude = market.latitude;
        const longitude = market.longitude;
        // Extend bounds
        const latLng = new maps.LatLng(latitude, longitude);
        bounds.extend(latLng);
      } catch (error) {
        console.error('Error processing market:', market.name, error);
      }
    });

    // Fit map to bounds if we have valid boundaries
    map.fitBounds(bounds);
  };

  const handleMapChange = ({ bounds }: { bounds: GoogleMapReact.Bounds }) => {
    setBounds(bounds);
    dispatch(
      getMarketsInMapViewport({
        northEast: {
          lat: bounds.ne.lat,
          lng: bounds.ne.lng,
        },
        southWest: {
          lat: bounds.sw.lat,
          lng: bounds.sw.lng,
        },
      } as Bounds)
    );
  };

  const marketMarkers = useMemo(() => {
    return [...visibleRankedMarkets, ...dedupedDynamicMarkets]
      .slice(0, maxMarketsToShow)
      .map((market, index) => {
        return (
          <MarketPointer
            key={index + '-market-explorer-pointer'}
            lat={market.latitude}
            lng={market.longitude}
            name={market.name}
            rank={market.rank ? market.rank : undefined}
            value={
              market.rank
                ? currentRequestType == 'premium' || selectedSortBy === 'supply'
                  ? formatMetricValue(market[selectedSortBy], selectedSortBy, currency)
                  : 'Locked'
                : `${abbreviateNumber(market.supply)} listings`
            }
            url={market.url ? market.url : market.market_url}
          />
        );
      });
  }, [visibleRankedMarkets, dedupedDynamicMarkets, currentRequestType, selectedSortBy, currency]);

  return (
    <Grid container item xs={12}>
      <Box sx={{ width: '100%', aspectRatio: '1 / 1' }}>
        {/* {isLoading && <Skeleton variant="rectangular" height={600} width="100%" animation="wave" />} */}
        <GoogleMapReact
          key="google-map-from-market-explorer-main"
          bootstrapURLKeys={{
            key: GOOGLE_MAPS_API_KEY,
            libraries: ['drawing', 'places', 'visualization'],
            id: 'market-explorer-map',
            v: '3.64',
          } as any}
          // this will be ignored so set it to the first market's coordinates
          center={{ lat: markets?.[0]?.latitude, lng: markets?.[0]?.longitude}}
          options={map => mapOptions(map, false)}
          zoom={10}
          resetBoundsOnResize={true}
          yesIWantToUseGoogleMapApiInternals
          onGoogleApiLoaded={({ map, maps }) => apiIsLoaded(map, maps, markets)}
          onChange={map => handleMapChange(map)}
          style={{ height: '100%', width: '100%' }}
        >
          {markets && markets.length > 0 && marketMarkers.length > 0 && marketMarkers}
        </GoogleMapReact>
      </Box>
    </Grid>
  );
};


export default React.memo(MapComponent);